// App.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';

import Home from './pages/Home';
import Result from './pages/Result';
import Report from './pages/Report';
import WalletRisk from './pages/WalletRisk';
import PathAnalysis from './pages/PathAnalysis';
import BlockchainAcademy from './pages/BlockchainAcademy';
import AIRiskPrediction from './pages/AIRiskPrediction';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import MyReports from './pages/MyReports';
import Favorites from './pages/Favorites';
import ReportDetails from './pages/ReportDetails';

import Navbar from './components/Navbar'; 
import Footer from './components/Footer';
import './i18n';

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FeedbackButton from './components/FeedbackButton';
import ScrollToTop from './components/ScrollToTop';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminArticles from './pages/AdminArticles';
import AdminVideos from './pages/AdminVideos';
import AdminCreateArticle from './pages/AdminCreateArticle';
import AdminCreateVideo from './pages/AdminCreateVideo';
import ArticlesList from './pages/ArticlesList';
import VideosList from './pages/VideosList';
import ArticleDetail from './pages/ArticleDetail';
import VideoDetail from './pages/VideoDetail';
import EditArticle from './pages/EditArticle';
import EditVideo from './pages/EditVideo';
import ResourcesList from './pages/ResourcesList';
import ApiUsagePage from './pages/ApiUsagePage';
import Maintenance from './pages/Maintenance';
import ErrorBoundaryWrapper from './components/ErrorBoundary';
import BecomePartner from './pages/BecomePartner';
import LoadingOverlay from './components/LoadingOverlay';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import BlockchainVisualization from './pages/BlockchainVisualization'; // 金流追蹤
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import OurServices from './pages/OurServices';
import PrivacyPolicy from './pages/PrivacyPolicy';
import UpdateLog from './pages/UpdateLog';
import AdminUpdateLog from './pages/AdminUpdateLog'; 
import Faqs from './pages/Faqs';
import CommonQuestionsPage from "./pages/CommonQuestionsPage"; // FAQs-常見問題頁面
import CaseReportPage from "./pages/CaseReportPage"; // FAQs-報案說明頁面
import FraudSupportPage from "./pages/FraudSupportPage"; // FAQs-詐騙支援頁面

import SolanaAlert from './pages/SolanaAlert/SolanaAlert';
import SolanaAlertDetail from './pages/SolanaAlert/SolanaAlertDetail';
import Log from './pages/SolanaAlert/Log';
import AddressAlert from './pages/AddressAlert'; // 錢包通知功能
import LoadingAnimations from "./pages/LoadingTest"; // 載入動畫測試頁面
import ReportList from './pages/ReportList';
import PublicReportDetails from './pages/PublicReportDetails';
import PricingPlans from './pages/PricingPlans';
import ApiPrice from './pages/ApiPrice';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import DonationPage from './pages/DonationPage';
import DonationSuccessPage from './pages/DonationSuccessPage';
import DonationTransparencyPage from './pages/DonationTransparencyPage';
import SeoManager from './components/admin/SeoManager';




// import CookieConsent from './components/CookieConsent';

// 初始化 Google Analytics
ReactGA.initialize('G-RBK4ZP0177');

const TrackRouteChange = () => {
  const location = useLocation();
  React.useEffect(() => {
    // 追蹤頁面瀏覽
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);
  return null;
};

function AppContent({ darkMode, toggleDarkMode }) {
  const { isLoading } = useLoading();
  
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-black'}`}>
      <LoadingOverlay isVisible={isLoading} />
      <Navbar toggleDarkMode={toggleDarkMode} darkMode={darkMode} />
      <div style={{ paddingTop: "6.5rem" }}>
        <Routes>
          <Route path="/" element={<Home darkMode={darkMode} />} />
          <Route path="/wallet-risk" element={<WalletRisk />} />
          <Route path="/result" element={<Result />} />
          <Route path="/report" element={<Report />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/my-reports" element={<MyReports />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/path-analysis" element={<PathAnalysis />} />
          <Route path="/academy" element={<BlockchainAcademy />} />
          <Route path="/ai-prediction" element={<AIRiskPrediction />} />
          <Route path="/report/:id" element={<ReportDetails />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/articles" element={<AdminArticles />} />
          <Route path="/admin/articles/create" element={<AdminCreateArticle />} />
          <Route path="/articles/:id" element={<ArticleDetail />} />
          <Route path="/admin/articles/edit/:id" element={<EditArticle />} />
          <Route path="/admin/videos" element={<AdminVideos />} />
          <Route path="/admin/videos/create" element={<AdminCreateVideo />} />
          <Route path="/academy/articles" element={<ArticlesList />} />
          <Route path="/academy/videos" element={<VideosList />} />
          <Route path="/academy/resources" element={<ResourcesList />} />
          <Route path="/videos/:id" element={<VideoDetail />} />
          <Route path="/admin/videos/edit/:id" element={<EditVideo />} />
          <Route path="/admin/api-usage" element={<ApiUsagePage />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/become-partner" element={<BecomePartner />} />
          <Route path="/blockchain-visualization" element={<BlockchainVisualization />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/services" element={<OurServices />} />
          <Route path="/solana-alert" element={<SolanaAlert />} />
          <Route path="/solana-alert/wallet/:address" element={<SolanaAlertDetail />} />
          <Route path="/solana-alert/log/:address" element={<Log />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/update-log" element={<UpdateLog />} />
          <Route path="/admin/update-log" element={<AdminUpdateLog darkMode={darkMode} />} />
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/faqs/common-questions" element={<CommonQuestionsPage />} />
          <Route path="/faqs/case-report" element={<CaseReportPage />} />
          <Route path="/faqs/fraud-support" element={<FraudSupportPage />} />
          <Route path="/loading-test" element={<LoadingAnimations />} />
          <Route path="/report-list" element={<ReportList />} />
          <Route path="/public-report-details/:id" element={<PublicReportDetails />} />
          <Route path="/AddressAlert" element={<AddressAlert />} />
          <Route path="/pricing-plans" element={<PricingPlans />} />
          <Route path="/api-price" element={<ApiPrice />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/donation" element={<DonationPage />} /> 
          <Route path="/donation/success" element={<DonationSuccessPage />} />
          <Route path="/donation-transparency" element={<DonationTransparencyPage />} />
          <Route path="/admin/seo-manager" element={<SeoManager />} />
        </Routes>
      </div>
      <Footer darkMode={darkMode} />
      <FeedbackButton />
      <ScrollToTop />
      <ToastContainer />
    </div>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    // 從 localStorage 讀取深淺模式設定，如果沒有則預設為 false
    const savedDarkMode = localStorage.getItem('darkMode');
    return savedDarkMode ? JSON.parse(savedDarkMode) : false;
  });

  const toggleDarkMode = () => {
    setDarkMode(prevDarkMode => {
      const newDarkMode = !prevDarkMode;
      // 保存新的深淺模式設定到 localStorage
      localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
      if (newDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newDarkMode;
    });
  };

  // 在組件掛載時根據保存的設定設置深淺模式
  React.useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  React.useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      if (process.env.NODE_ENV === 'production') {
        // 只有在生產環境時才跳轉到維修頁面
        window.location.href = '/maintenance';
      } else {
        // 開發環境打印錯誤但不跳轉
        console.warn('Unhandled Promise Rejection in development:', event.reason);
      }
    };
  
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
  
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundaryWrapper>
      <HelmetProvider>
        <Router>
          <TrackRouteChange />
          <LoadingProvider>
            <Toaster position="top-right" />
            <AppContent darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          </LoadingProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundaryWrapper>
  );
}

export default App;
